import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Row as TanTableRow, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import {
  FieldError,
  FieldErrorsImpl,
  Merge,
  useFieldArray,
  useForm,
} from "react-hook-form";
import * as yup from "yup";

// SecurityType options that actually exist in the database
const securityTypeOptions = [
  "Alternative",
  "Bond",
  "Cash",
  "Cash Equivalent",
  "Crypto",
  "Equity",
  "ETF",
  "Fund",
  "Mutual Fund",
  "Stock",
  "Unknown",
];

interface MinimumTradeSize {
  id: number;
  firmId: number;
  securityType?: string | null;
  securitySymbol?: string | null;
  minimumAmount: number;
  created: string | Date;
  updated: string | Date;
  _isDeleted?: boolean;
  _isNew?: boolean;
}
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import FormError from "../components/FormError";
import FormFieldError from "../components/FormFieldError";
import SymbolField from "../components/SymbolField";
import TabContainerWithTabs from "../components/TabContainer";
import EditableTableRow, {
  EditableRowExtension,
} from "../components/Table/EditableTableRow";
import { Table, useTable } from "../components/Table/Table";
import {
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../lib/api";
import { useRoles } from "../lib/auth";
import { formatCurrency } from "../lib/numbers";
import FirmNav from "./FirmNav";

type MinimumTradeSizeRow = MinimumTradeSize &
  EditableRowExtension & {
    _isDeleted?: boolean;
  };
type MinimumTradeSizeFields = {
  securityType?: string | null;
  securitySymbol?: string | null;
  minimumAmount: number;
};

const schema: yup.ObjectSchema<{
  minimumTradeSizes: MinimumTradeSizeFields[];
}> = yup.object({
  minimumTradeSizes: yup
    .array()
    .of(
      yup.object({
        securityType: yup.string().nullable().label("Security Type"),
        securitySymbol: yup.string().nullable().label("Security Symbol"),
        minimumAmount: yup
          .number()
          .required()
          .integer("Minimum amount must be a whole number")
          .min(0, "Minimum amount must be positive")
          .label("Minimum Amount"),
      }),
    )
    .required(),
});

const MinimumTradeSizes = () => {
  const roles = useRoles();
  const {
    control,
    register,
    reset,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });
  const { insert: insertField, remove: removeField } = useFieldArray({
    name: "minimumTradeSizes",
    control,
    keyName: "_field_id",
  });

  const [minimumTradeSizesEdited, setMinimumTradeSizes] = useState<
    MinimumTradeSize[]
  >([]);
  const [changedRules, setChangedRules] = useState<
    Record<string, MinimumTradeSize>
  >({});

  const handleUpdateMinimumTradeSize = useCallback(
    (
      index: number,
      securityType: string | null,
      securitySymbol: string | null,
      minimumAmount: number,
    ) => {
      setMinimumTradeSizes(
        minimumTradeSizesEdited.map((rule, indexInner) => {
          if (index !== indexInner) {
            return rule;
          }

          const updatedRule = {
            ...rule,
            securityType,
            securitySymbol,
            minimumAmount,
            // Keep the original _isNew value - don't override it
          };

          setChangedRules((changedRules) => ({
            ...changedRules,
            [rule.id]: updatedRule,
          }));

          return updatedRule;
        }),
      );
    },
    [minimumTradeSizesEdited],
  );

  const onConfirmRow = useCallback(
    (row: TanTableRow<MinimumTradeSizeRow>) => {
      const vals = getValues(`minimumTradeSizes.${row.index}`);
      handleUpdateMinimumTradeSize(
        row.index,
        vals.securityType || null,
        vals.securitySymbol || null,
        vals.minimumAmount,
      );
    },
    [getValues, handleUpdateMinimumTradeSize],
  );

  const onEditRow = useCallback(
    (row: TanTableRow<MinimumTradeSizeRow>) => {
      setValue(`minimumTradeSizes.${row.index}`, row.original);
    },
    [setValue],
  );

  const onDiscardRow = useCallback(
    (row: TanTableRow<MinimumTradeSizeRow>) => {
      setValue(`minimumTradeSizes.${row.index}`, row.original);
    },
    [setValue],
  );

  const onAddRow = useCallback(() => {
    const newRow = {
      id: Date.now(), // temporary ID for new rows
      firmId: 0, // will be set by backend
      securityType: null,
      securitySymbol: null,
      minimumAmount: 0,
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
      _isNew: true,
    };

    insertField(0, newRow, { shouldFocus: true });
    setMinimumTradeSizes([newRow, ...minimumTradeSizesEdited]);
  }, [insertField, minimumTradeSizesEdited]);

  const onRemoveRow = useCallback(
    (row: TanTableRow<MinimumTradeSizeRow>) => {
      removeField(row.index);
      setMinimumTradeSizes(
        minimumTradeSizesEdited.filter((rule, index) => index !== row.index),
      );

      if (row.original._isNew) {
        delete changedRules[row.original.id];
        setChangedRules(changedRules);
      } else {
        // Mark for deletion
        setChangedRules({
          ...changedRules,
          [row.original.id]: { ...row.original, _isDeleted: true },
        });
      }
    },
    [changedRules, removeField, minimumTradeSizesEdited],
  );

  const securityTypeSelectOptions = useMemo(
    () => [
      <option key="" value="">
        -
      </option>,
      ...securityTypeOptions
        .filter((type) => type !== "") // Remove empty string since we have firm-wide option
        .sort()
        .map((securityType) => (
          <option key={securityType} value={securityType}>
            {securityType}
          </option>
        )),
    ],
    [],
  );

  const columnHelper = useMemo(
    () => createColumnHelper<MinimumTradeSizeRow>(),
    [],
  );

  const tradeSizeErrors = errors.minimumTradeSizes as Merge<
    FieldError,
    (Merge<FieldError, FieldErrorsImpl<MinimumTradeSizeRow>> | undefined)[]
  >;

  const columns = useMemo(
    () => [
      columnHelper.accessor("securityType", {
        header: () => "Security Type",
        cell: (info) => {
          const value = info.getValue();
          return value || "-";
        },
        filterFn: (row, columnId, filterValue) => {
          const value = row.getValue(columnId) as string;
          if (!filterValue) return true;
          if (!value)
            return "-".toLowerCase().includes(filterValue.toLowerCase());
          return value.toLowerCase().includes(filterValue.toLowerCase());
        },
        meta: {
          editableComponent: (cell) => {
            const symbolValue = getValues(
              `minimumTradeSizes.${cell.row.index}.securitySymbol`,
            );
            const isTypeDisabled = Boolean(symbolValue);

            return (
              <>
                <Form.Select
                  {...register(
                    `minimumTradeSizes.${cell.row.index}.securityType`,
                  )}
                  disabled={isTypeDisabled}
                  onChange={(e) => {
                    // Clear security symbol when type is selected
                    if (e.target.value) {
                      setValue(
                        `minimumTradeSizes.${cell.row.index}.securitySymbol`,
                        "",
                      );
                    }
                    // Let react-hook-form handle the type value
                    register(
                      `minimumTradeSizes.${cell.row.index}.securityType`,
                    ).onChange(e);
                  }}
                >
                  {securityTypeSelectOptions}
                </Form.Select>
                <FormFieldError
                  field={tradeSizeErrors?.[cell.row.index]?.securityType}
                />
                {isTypeDisabled && (
                  <Form.Text className="text-muted">
                    Disabled when security symbol is selected
                  </Form.Text>
                )}
              </>
            );
          },
        },
      }),
      columnHelper.accessor("securitySymbol", {
        header: () => "Security Symbol",
        cell: (info) => info.getValue() || "-",
        meta: {
          editableComponent: (cell) => {
            const typeValue = getValues(
              `minimumTradeSizes.${cell.row.index}.securityType`,
            );
            const isDisabled = Boolean(typeValue);

            return (
              <>
                <SymbolField
                  fieldName={`minimumTradeSizes.${cell.row.index}.securitySymbol`}
                  register={register}
                  control={control}
                  getValues={getValues}
                  disabled={isDisabled}
                  onSelect={(suggestion) => {
                    // Clear security type when symbol is selected
                    if (suggestion.symbol) {
                      setValue(
                        `minimumTradeSizes.${cell.row.index}.securityType`,
                        "",
                      );
                    }
                  }}
                />
                <FormFieldError
                  field={tradeSizeErrors?.[cell.row.index]?.securitySymbol}
                />
                {isDisabled && (
                  <Form.Text className="text-muted">
                    Disabled when security type is selected
                  </Form.Text>
                )}
              </>
            );
          },
        },
      }),
      columnHelper.accessor((row) => row.minimumAmount ?? 0, {
        id: "minimumAmount",
        header: () => "Minimum Amount",
        cell: (info) => formatCurrency(info.getValue(), 0),
        meta: {
          editableComponent: (cell) => (
            <>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                {...register(
                  `minimumTradeSizes.${cell.row.index}.minimumAmount`,
                  { valueAsNumber: true },
                )}
              />
              <FormFieldError
                field={tradeSizeErrors?.[cell.row.index]?.minimumAmount}
              />
            </>
          ),
        },
      }),
      columnHelper.accessor("updated", {
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "-";
          const date = typeof value === "string" ? new Date(value) : value;
          return date.toLocaleDateString();
        },
        header: () => "Updated",
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        size: 120,
      }),
      columnHelper.display({
        id: "add-remove",
      }),
    ],
    [
      columnHelper,
      register,
      control,
      getValues,
      setValue,
      tradeSizeErrors,
      securityTypeSelectOptions,
    ],
  );

  const {
    isPending: isPendingMinimumTradeSizes,
    isError: isErrorMinimumTradeSizes,
    data: dataMinimumTradeSizes,
  } = useAuthenticatedFetch<{ data: MinimumTradeSize[] }>(
    "/firm/trades/minimum-sizes",
  );

  const minimumTradeSizes = useMemo(
    () => dataMinimumTradeSizes?.data ?? [],
    [dataMinimumTradeSizes?.data],
  );

  useEffect(() => {
    if (
      !isPendingMinimumTradeSizes &&
      typeof dataMinimumTradeSizes?.data !== "undefined"
    ) {
      reset({ minimumTradeSizes });
      setMinimumTradeSizes(minimumTradeSizes);
    }
  }, [
    isPendingMinimumTradeSizes,
    dataMinimumTradeSizes?.data,
    minimumTradeSizes,
    reset,
  ]);

  const { table } = useTable({
    columns: columns,
    data: minimumTradeSizesEdited,
    initialState: {
      pagination: {
        pageSize: 40,
      },
    },
    autoResetAll: false,
  });

  const createMutation = useAuthenticatedMutationAsync<{
    data: MinimumTradeSize;
  }>(
    "/firm/trades/minimum-sizes",
    async (
      data: Omit<
        MinimumTradeSize,
        "id" | "firmId" | "created" | "updated" | "_isNew" | "_isDeleted"
      >,
    ) => ({
      method: "POST",
      body: JSON.stringify(data),
    }),
  );

  const { getAccessTokenSilently } = useAuth0();

  const updateRule = useCallback(
    async (rule: MinimumTradeSize) => {
      const { firmId, created, updated, _isNew, _isDeleted, ...updateData } =
        rule;
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/firm/trades/minimum-sizes/${rule.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        },
      );
      if (!response.ok) throw new Error("Failed to update rule");
      return response.json();
    },
    [getAccessTokenSilently],
  );

  const deleteRule = useCallback(
    async (id: number) => {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/firm/trades/minimum-sizes/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) throw new Error("Failed to delete rule");
      return response.json();
    },
    [getAccessTokenSilently],
  );

  const handleDiscardChanges = useCallback(() => {
    setMinimumTradeSizes(minimumTradeSizes);
    reset({ minimumTradeSizes });
    setChangedRules({});
  }, [reset, minimumTradeSizes]);

  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);

  const onSubmit = useMutation({
    mutationFn: async () => {
      if (isValid) {
        try {
          console.log("Submitting changes:", changedRules);
          // Process each changed rule
          for (const rule of Object.values(changedRules)) {
            console.log("Processing rule:", rule);
            if (rule._isDeleted) {
              console.log("Deleting rule:", rule.id);
              await deleteRule(rule.id);
            } else if (rule._isNew) {
              const {
                id,
                firmId,
                created,
                updated,
                _isNew,
                _isDeleted,
                ...createData
              } = rule;
              console.log("Creating rule:", createData);
              await createMutation(createData);
            } else {
              console.log("Updating rule:", rule.id);
              await updateRule(rule);
            }
          }

          queryClient.invalidateQueries({
            queryKey: ["/firm/trades/minimum-sizes"],
          });
          setChangedRules({});
          notificationContext.pushNotification({
            id: "minimum-trade-sizes",
            header: "Minimum Trade Sizes Updated",
            body: "Minimum trade size rules updated successfully",
            variant: "success",
          });
        } catch (err) {
          console.error("Failed to save minimum trade sizes", err);
          notificationContext.pushNotification({
            id: "minimum-trade-sizes",
            header: "Failed to Save Minimum Trade Sizes",
            body: "Minimum trade size rules not saved",
            variant: "danger",
          });
        }
      }
    },
  });

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      {isPendingMinimumTradeSizes ? (
        <Loading />
      ) : isErrorMinimumTradeSizes ? (
        <Alert variant="danger">
          Failed to retrieve minimum trade size rules
        </Alert>
      ) : (
        <>
          <Row className="mb-3">
            <Col>
              <h4>Minimum Trade Size Configuration</h4>
              <p className="text-muted">
                Configure minimum trade sizes for rebalancing. Rules are applied
                hierarchically: security-specific → type-specific → firm-wide.
                There can be only one firm-wide.
              </p>
            </Col>
          </Row>
          {!roles.includes("Admin") ||
          Object.keys(changedRules).length <= 0 ? null : (
            <Row className="mb-2">
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="success"
                  onClick={() => onSubmit.mutateAsync()}
                  disabled={onSubmit.isPending}
                >
                  Submit{" "}
                  {onSubmit.isPending ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    ""
                  )}
                </Button>
              </Col>
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="danger"
                  onClick={handleDiscardChanges}
                  disabled={onSubmit.isPending}
                >
                  Discard
                </Button>
              </Col>
            </Row>
          )}
          <Row>
            <Col
              style={{ pointerEvents: onSubmit.isPending ? "none" : "auto" }}
            >
              <FormError
                hasError={!isValid}
                message="Please fix the errors above."
              />
              <Table
                table={table}
                RowComp={roles.includes("Admin") ? EditableTableRow : undefined}
                onSave={onConfirmRow}
                onEdit={onEditRow}
                onDiscard={onDiscardRow}
                enableAddRemove
                onAdd={onAddRow}
                onRemove={onRemoveRow}
              />
            </Col>
          </Row>
        </>
      )}
    </TabContainerWithTabs>
  );
};

export default MinimumTradeSizes;
